import { Modal, BodyLargeEmphasized } from '@/design-system';
import { GradeField, GradeRangeSelector } from '@/components';
import { FilterOption, OptionType } from '@/components/FlyerboardFilters/FilterOption';
import { Divider } from '@/components/Divider';
import { css, cx } from '@emotion/css';
import React, { Fragment } from 'react';
import { OwningTypeEnum } from '@/graphql/types';

type JarFilterModalProps = {
  isOpen: boolean;
  onToggle: (state: boolean) => void;
  filters: {
    cost: string[];
    provider: string[];
    types: string[];
    subscriptions: { id: string; type: string }[];
  };
  setFilters: (filters: any) => void;
  gradeFilter: { from?: string; to?: string };
  setGradeFilter: (gradeFilter: any) => void;
  loading: boolean;
  viewResultsLabel: string;
  resetFilters: () => void;
  viewResults: () => void;
  handleToggleOption: (section: string, value: string, type?: string) => void;
  handleGradeChange: (field: 'from' | 'to', value?: string) => void;
  generateProviderOptions: () => OptionType[];
  generateCostOption: () => OptionType[];
  generateTypesOptions: () => OptionType[];
  disableClearAllButton: boolean;
  grades: any[];
  lowGrade: string;
  highGrade: string;
  setDefaultGrades: () => void;
  renderSubscriptionList: () => React.ReactNode;
  subscriptionsLength: number;
};

export const JarFilterModal: React.FC<JarFilterModalProps> = props => {
  const {
    isOpen,
    onToggle,
    filters,
    gradeFilter,
    loading,
    viewResultsLabel,
    resetFilters,
    viewResults,
    handleToggleOption,
    handleGradeChange,
    generateProviderOptions,
    generateCostOption,
    generateTypesOptions,
    disableClearAllButton,
    grades,
    renderSubscriptionList,
    subscriptionsLength,
    lowGrade,
    highGrade,
  } = props;

  const costOptions = generateCostOption();
  const providerOptions = generateProviderOptions();
  const typesOptions = generateTypesOptions();

  return (
    <Modal
      isOpen={isOpen}
      title="Filters"
      confirmButtonText={viewResultsLabel}
      cancelButtonText="Reset Filters"
      onCancel={resetFilters}
      onConfirm={viewResults}
      onClose={() => onToggle(false)}
      disableCancelButton={disableClearAllButton}
      loading={loading}
      className={styles.modal}
    >
      <div className={styles.container}>
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>
            <BodyLargeEmphasized className={styles.filterName} id="filter-provider-label">
              Posted by
            </BodyLargeEmphasized>
          </legend>
          <div className={styles.filtersContainer}>
            <div className={styles.flexColumn}>
              {providerOptions
                .slice(0, Math.ceil(providerOptions.length / 2))
                .map(({ value, description, disabled = false }) => {
                  const isChecked = filters.provider.includes(value);
                  const filterName = 'provider';
                  return (
                    <Fragment key={value}>
                      <FilterOption
                        key={value}
                        description={description}
                        value={value}
                        filterName={filterName}
                        checked={isChecked}
                        handleToggle={() => () => handleToggleOption(filterName, value)}
                        disabled={disabled && !isChecked}
                      />
                    </Fragment>
                  );
                })}
            </div>
            <div className={styles.flexColumn}>
              {providerOptions
                .slice(Math.ceil(providerOptions.length / 2))
                .map(({ value, description, disabled = false }) => {
                  const isChecked = filters.provider.includes(value);
                  return (
                    <FilterOption
                      key={value}
                      description={description}
                      value={value}
                      filterName="provider"
                      checked={isChecked}
                      handleToggle={() => () => handleToggleOption('provider', value)}
                      disabled={disabled && !isChecked}
                      placeholder="(Organization)"
                    />
                  );
                })}
            </div>
          </div>
          {subscriptionsLength > 1 && (
            <fieldset className={cx(styles.fieldset, styles.sentToSection)}>
              <legend className={styles.legend}>
                <BodyLargeEmphasized className={styles.filterName} id="filter-sentTo-label">
                  Sent to
                </BodyLargeEmphasized>
              </legend>
              <div className={styles.optionsContainer}>{renderSubscriptionList()}</div>
            </fieldset>
          )}
          <div className={styles.divider}>
            <Divider />
          </div>
        </fieldset>
        {costOptions.length > 0 && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>
              <BodyLargeEmphasized className={styles.filterName} id="filter-cost-label">
                Cost
              </BodyLargeEmphasized>
            </legend>
            <div className={styles.optionsContainer}>
              {costOptions.map(({ value, description, disabled = false }) => {
                const isChecked = filters.cost.includes(value);
                return (
                  <FilterOption
                    key={value}
                    description={description}
                    value={value}
                    filterName="cost"
                    checked={isChecked}
                    handleToggle={() => () => handleToggleOption('cost', value)}
                    disabled={disabled && !isChecked}
                  />
                );
              })}
            </div>
            <div className={styles.divider}>
              <Divider />
            </div>
          </fieldset>
        )}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>
            <BodyLargeEmphasized className={styles.filterName} id="filter-grade-label">
              Grade
            </BodyLargeEmphasized>
          </legend>
          <div className={styles.gradeSelectorContainer}>
            <GradeRangeSelector
              fromGrade={gradeFilter.from}
              toGrade={gradeFilter.to}
              onChange={handleGradeChange}
              grades={grades}
              initialValue={{ from: lowGrade, to: highGrade }}
            />
          </div>
          <div className={styles.divider}>
            <Divider />
          </div>
        </fieldset>

        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>
            <BodyLargeEmphasized className={styles.filterName} id="filter-types-label">
              Types
            </BodyLargeEmphasized>
          </legend>
          <div className={styles.filtersContainer}>
            <div className={styles.flexColumn}>
              {typesOptions
                .slice(0, Math.ceil(typesOptions.length / 2))
                .map(({ value, description }) => {
                  const isChecked = filters.types.includes(value);
                  return (
                    <FilterOption
                      key={value}
                      description={description}
                      value={value}
                      filterName="types"
                      checked={isChecked}
                      handleToggle={() => () => handleToggleOption('types', value)}
                    />
                  );
                })}
            </div>
            <div className={styles.flexColumn}>
              {typesOptions
                .slice(Math.ceil(typesOptions.length / 2))
                .map(({ value, description }) => {
                  const isChecked = filters.types.includes(value);
                  return (
                    <FilterOption
                      key={value}
                      description={description}
                      value={value}
                      filterName="types"
                      checked={isChecked}
                      handleToggle={() => () => handleToggleOption('types', value)}
                    />
                  );
                })}
            </div>
          </div>
        </fieldset>
      </div>
    </Modal>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
  `,
  filterName: css`
    text-transform: capitalize;
  `,
  optionsContainer: css`
    padding-top: 16px;
  `,
  sentToSection: css`
    margin-top: 16px;
  `,
  gradeSelectorContainer: css`
    padding-top: 16px;
  `,
  filtersContainer: css`
    display: flex;
    flex-wrap: wrap;
    padding-top: 16px;
  `,
  divider: css`
    padding: 24px 0;
  `,
  fieldset: css`
    border: 0;
    margin: 0;
    padding: 0;
    min-inline-size: 0;
  `,
  legend: css`
    margin: 0;
    padding: 0;
  `,
  flexColumn: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-right: 10px;

    &:last-child {
      padding-right: 0;
    }
  `,
  modal: css`
    width: 680px;
  `,
};
